import React from 'react';
import { cn } from '@/utils/shad';
import useSwipeScroll from '@/hooks/use-drag-scroll';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { variant?: 'grid' | 'first-column-focus' }
>(({ className, variant, ...props }, ref) => {
  const tableRef = React.useRef<HTMLTableElement>(null);
  useSwipeScroll({ sliderRef: tableRef });

  return (
    <div ref={tableRef} className="relative w-full">
      <table
        ref={ref}
        className={cn(
          'w-full caption-bottom text-sm',
          variant === 'first-column-focus' &&
            'h-12 text-[14px] [&_td]:border-x [&_td]:border-lightborder [&_td:first-child]:border-l-0 [&_td:first-child]:text-[12px] [&_td:last-child]:border-r-0 [&_td:not(:first-child)]:text-[10px] [&_th]:border-x [&_th]:border-lightborder [&_th:first-child]:border-l-0 [&_th:last-child]:border-r-0 [&_th:not(:first-child)]:w-0 [&_th:not(:first-child)]:text-[12px] [&_tr]:border-lightborder [&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pl-8',
          variant === 'grid' &&
            'h-12 [&_td]:border-r [&_td]:border-lightborder [&_td:first-child]:border-l-0 [&_td:last-child]:border-r-0 [&_th]:border-r [&_th]:border-lightborder [&_th:first-child]:border-l-0 [&_th:last-child]:border-r-0 [&_tr]:h-12 [&_tr]:border-lightborder [&_tr>*:first-child]:pl-8',
          className,
        )}
        {...props}
      />
    </div>
  );
});
Table.displayName = 'Table';

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn('[&_tr]:border-b', className)} {...props} />,
);
TableHeader.displayName = 'TableHeader';

export type TableBodyProps = React.HTMLAttributes<HTMLTableSectionElement> & {
  /** Zebra striping like report datatables: odd #0E0E0E, even neutral-950. Set false for custom row backgrounds (e.g. nested/collapsible rows). */
  striped?: boolean;
};

const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, striped = true, ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn(
        '[&_tr:last-child]:border-b',
        striped &&
          '[&_tr:nth-child(even)]:bg-neutral-950 [&_tr:nth-child(odd)]:bg-[#0E0E0E] [&_tr[data-state=selected]]:bg-muted!',
        className,
      )}
      {...props}
    />
  ),
);
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn('border-t border-b bg-muted/50 font-medium last:[&>tr]:border-b-0', className)}
      {...props}
    />
  ),
);
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn('group/row border-b transition-colors data-[state=selected]:bg-muted', className)}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'h-12 max-h-12 px-3 py-0 text-left align-middle text-[12px] font-medium whitespace-nowrap text-muted-foreground has-[[role=checkbox]]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'p-1 px-3 align-middle text-foreground has-[[role=checkbox]]:pr-0 *:[[role=checkbox]]:translate-y-0.5',
        className,
      )}
      {...props}
    />
  ),
);
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
  ),
);
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
